import type { FC, PropsWithChildren } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Loader } from '@cognite/cogs-core-v11/components/loader/loader.tsx';

import { CdmSelectedLocationContext } from './cdm-selected-location-context';
import { SelectedLocationProviderContext } from './cdm-selected-location-provider.context';

export const CdmSelectedLocationProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { useLocations, AppConfigErrorScreen } = useContext(
    SelectedLocationProviderContext
  );
  const { t } = useTranslation('user');

  const {
    selectedLocationConfig,
    allLocationConfigs,
    isLoading,
    isError,
    locationOptions,
    selectedLocationOption,
    handleSaveLocation,
  } = useLocations();

  const contextValue = useMemo(
    () => ({
      selectedLocationOption,
      locationOptions,
      handleSaveLocation,
      selectedLocationConfig,
      allLocationConfigs,
      isLoading,
    }),
    [
      selectedLocationOption,
      locationOptions,
      handleSaveLocation,
      selectedLocationConfig,
      allLocationConfigs,
      isLoading,
    ]
  );

  if (isLoading) {
    return (
      <Loader
        infoText={
          <Heading level={6}>
            {t('loadingLocations', 'Loading locations')}
          </Heading>
        }
      />
    );
  }

  if (isError) {
    return (
      <AppConfigErrorScreen
        title={t('errorLoadingLocationsTitle', 'Failed to load locations')}
        body={t(
          'errorLoadingLocationsDescription',
          'An error occurred while loading locations. Please try again or contact support.'
        )}
      />
    );
  }

  if (allLocationConfigs.length === 0) {
    return (
      <AppConfigErrorScreen
        title={t('noLocationsConfiguredTitle', 'No locations configured')}
        body={t(
          'noLocationsConfiguredDescription',
          'There are no locations configured for this application. Please contact an administrator to set them up.'
        )}
      />
    );
  }
  return (
    <CdmSelectedLocationContext.Provider value={contextValue}>
      {children}
    </CdmSelectedLocationContext.Provider>
  );
};
